import {
  Component,
  Host,
  Prop,
  Element,
  State,
  getAssetPath,
  Method,
  h
} from "@stencil/core";

import { MercuryBundles, getIconPath } from "@genexus/mercury";
import {
  TreeViewItemModel,
  TreeViewItemModelExtended
} from "@genexus/chameleon-controls-library";

import { Locale } from "../../common/locale";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/tree-view",
  "utils/form--full",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

const SERVER_ICON = getIconPath({
  category: "system",
  name: "computer",
  colorType: "primary"
});

const DATABASE_ICON = getIconPath({
  category: "system",
  name: "database",
  colorType: "success"
});

const LOCAL_SERVERS_NODE_ID = "local-servers";
const NETWORK_SERVERS_NODE_ID = "network-servers";

@Component({
  tag: "gx-ide-db-instance-selector",
  styleUrl: "db-instance-selector.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/db-instance-selector"]
})
export class GxIdeDbInstanceSelector {
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  #chShortcutsEl: HTMLChShortcutsElement;
  #shortcutsSrc = getAssetPath(
    `./gx-ide-assets/db-instance-selector/shortcuts.json`
  );

  #selectedInstanceName: string | undefined;

  @Element() el: HTMLGxIdeDbInstanceSelectorElement;

  @State() treeModel: TreeViewItemModel[] = [];
  @State() selectedInstanceId: string | undefined;

  /**
   * Callback invoked to load the available local server instances.
   * Should return the list of database instances available on local servers.
   */
  @Prop() readonly getLocalServersCallback!: GetServersCallback;

  /**
   * Callback invoked to load the available network server instances.
   * Should return the list of database instances available on network servers.
   */
  @Prop() readonly getNetworkServersCallback!: GetServersCallback;

  /**
   * Callback invoked when the user cancels the selection.
   */
  @Prop() readonly cancelCallback!: CancelCallback;

  /**
   * Callback invoked when the user confirms the selection. Receives the name
   * of the selected database instance.
   */
  @Prop() readonly confirmCallback!: ConfirmCallback;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.treeModel = [
      {
        id: LOCAL_SERVERS_NODE_ID,
        caption: this.#componentLocale.tree.localServers,
        startImgSrc: SERVER_ICON,
        leaf: false,
        lazy: true,
        expanded: false,
        items: []
      },
      {
        id: NETWORK_SERVERS_NODE_ID,
        caption: this.#componentLocale.tree.networkServers,
        startImgSrc: SERVER_ICON,
        leaf: false,
        lazy: true,
        expanded: false,
        items: []
      }
    ];
  }

  /**
   * Suspends or reactivates the shortcuts.
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    this.#chShortcutsEl.suspend = suspendShortcuts;
  }

  #lazyLoadTreeItems = async (
    treeItemId: string
  ): Promise<TreeViewItemModel[]> => {
    let servers: DbServerInstance[] = [];

    if (treeItemId === LOCAL_SERVERS_NODE_ID) {
      servers = (await this.getLocalServersCallback?.()) ?? [];
    } else if (treeItemId === NETWORK_SERVERS_NODE_ID) {
      servers = (await this.getNetworkServersCallback?.()) ?? [];
    }

    return servers.map(server => ({
      id: `${treeItemId}__${server.id}`,
      caption: server.name,
      startImgSrc: DATABASE_ICON,
      leaf: true,
      metadata: server.name
    }));
  };

  #selectedItemsChangeHandler = (
    event: CustomEvent<TreeViewItemModelExtended[]>
  ) => {
    const selected = event.detail.find(node => node.item.leaf);
    if (selected) {
      this.selectedInstanceId = selected.item.id;
      this.#selectedInstanceName =
        (selected.item.metadata as string) ?? selected.item.caption;
    } else {
      this.selectedInstanceId = undefined;
      this.#selectedInstanceName = undefined;
    }
  };

  #cancelButtonClickedHandler = () => {
    this.cancelCallback?.();
  };

  #confirmButtonClickedHandler = () => {
    if (this.#selectedInstanceName) {
      this.confirmCallback?.(this.#selectedInstanceName);
    }
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>
        <section class="section">
          <header class="dialog-header spacing-body dialog-header">
            <h1 class="label">{this.#componentLocale.header.title}</h1>
          </header>

          <div class="main">
            <ch-tree-view-render
              class="tree-view"
              model={this.treeModel}
              dragDisabled={true}
              dropDisabled={true}
              showLines="all"
              lazyLoadTreeItemsCallback={this.#lazyLoadTreeItems}
              onSelectedItemsChange={this.#selectedItemsChangeHandler}
            ></ch-tree-view-render>
          </div>

          <footer class="dialog-footer dialog-footer-with-border spacing-body-block-end spacing-body-inline">
            <div class="buttons-spacer">
              <button
                class="button-secondary"
                id="button-cancel"
                onClick={this.#cancelButtonClickedHandler}
                part="button-cancel"
              >
                {this.#componentLocale.footer.btnCancel}
              </button>
              <button
                class="button-primary"
                id="button-confirm"
                onClick={this.#confirmButtonClickedHandler}
                disabled={!this.selectedInstanceId}
                part="button-confirm"
              >
                {this.#componentLocale.footer.btnConfirm}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}

export type DbServerInstance = {
  id: string;
  name: string;
};

export type GetServersCallback = () =>
  | Promise<DbServerInstance[]>
  | DbServerInstance[];

export type CancelCallback = () => void | Promise<void>;

export type ConfirmCallback = (
  selectedInstanceName: string
) => void | Promise<void>;
